import { formatMoney } from '@betterment/js-runtime';
import { Box, Icon, ListRow, Text, TextColorToken } from '@betterment/samba-react';
import { useColorMode } from '@betterment/shared-components/src/components/color-mode/ColorModeProvider';

import { AccountRow } from './AccountRow';
import { LeadingIcon } from './LeadingIcon';

import { EnvelopeStatusStyle } from '../../../graphql/schema';
import MessageCard from '../../../InAppMessages/MessageCard';
import { StyledLinkRow, titleCss } from '../../../Shared/components-v2/styled-link-row/StyledLinkRow';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { EnvelopesCardQuery } from '../graphql/query.envelopesCard.generated';

type Envelope = EnvelopesCardQuery['envelopes'][number];
export const statusTextColor: Record<EnvelopeStatusStyle, TextColorToken> = {
  DANGER: 'text.weak',
  NEUTRAL: 'text',
  SUCCESS: 'text.success',
};

export const EnvelopeRow: React.FC<{ envelope: Envelope }> = ({ envelope }) => {
  const { colorMode } = useColorMode();
  const { accounts, accountsDescription, balance, purpose, hasRecurringTransaction } = envelope;

  if (!purpose) {
    return accounts.map(account => (
      <AccountRow
        key={account.id}
        account={account}
        hasRecurringTransaction={envelope.accounts.length === 1 && hasRecurringTransaction}
      />
    ));
  }

  return (
    <StyledLinkRow to={urlBuilder.appPurpose.absolute({ purposeId: purpose.id })} name={purpose.name}>
      <ListRow.LeadingAsset>
        <LeadingIcon url={purpose.image[colorMode]} />
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text color="text" css={titleCss}>
          {purpose.name}
        </Text>
        {accountsDescription && (
          <Text variant="text50" color="text.weak">
            {accountsDescription}
          </Text>
        )}
      </ListRow.LeadingContent>
      <ListRow.TrailingContent>
        <Box css={{ display: 'flex', alignItems: 'center', height: '100%', gap: 'space.100' }}>
          {hasRecurringTransaction && <Icon name="ArrowCircle" color="icon.success" size="xsmall" />}
          {balance && <Text color="text">{formatMoney(balance)}</Text>}
        </Box>
        {envelope.status?.shortDescription && (
          <Text color={statusTextColor[envelope.status.style]} variant="text50">
            {envelope.status.shortDescription}
          </Text>
        )}
      </ListRow.TrailingContent>
      <ListRow.BottomContent css={{ mt: 'space.0' }}>
        <MessageCard location="summaryAccountLevelBanner" goalId={purpose.legacyGoalId} />
      </ListRow.BottomContent>
    </StyledLinkRow>
  );
};
